'use client';

// Mot de passe oublié : saisie e-mail → envoi d'un code (réponse uniforme,
// anti-énumération) → écran de réinitialisation.

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Text } from '@/components/ui/Text';
import { useT } from '@/hooks/useT';
import { authErrorKey } from '@/lib/authError';
import { useForgotPassword } from '@/queries/auth';
import { useAuthFlow } from '@/stores/authFlow';

interface FormValues {
  email: string;
}

export default function ForgotPasswordPage() {
  const { t } = useT('auth');
  const router = useRouter();
  const forgot = useForgotPassword();
  const setPendingEmail = useAuthFlow((s) => s.setPendingEmail);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();

  const onSubmit = handleSubmit(async ({ email }) => {
    setFormError(null);
    try {
      await forgot.mutateAsync(email);
      setPendingEmail(email);
      router.push('/reset-password');
    } catch (e) {
      setFormError(t(`errors.${authErrorKey(e)}`));
    }
  });

  return (
    <div className="flex flex-col gap-8">
      <header className="flex flex-col gap-2">
        <Text as="h1" variant="displayXl" className="font-display">
          {t('forgot.title')}
        </Text>
        <Text variant="bodyLg" className="text-text-secondary">
          {t('forgot.subtitle')}
        </Text>
      </header>

      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <Input
          label={t('forgot.email')}
          type="email"
          autoComplete="email"
          placeholder={t('signIn.emailPlaceholder')}
          error={errors.email ? t(`validation.${errors.email.type === 'pattern' ? 'emailInvalid' : 'emailRequired'}`) : undefined}
          {...register('email', { required: true, pattern: /.+@.+\..+/ })}
        />

        {formError ? (
          <Text variant="bodySm" className="text-danger" role="alert">
            {formError}
          </Text>
        ) : null}

        <Button type="submit" fullWidth loading={forgot.isPending}>
          {t('forgot.submit')}
        </Button>
      </form>

      <Text variant="bodySm" className="text-center">
        <Link href="/sign-in" className="text-action hover:underline">
          {t('forgot.back')}
        </Link>
      </Text>
    </div>
  );
}
